/*
 * 模块: 批量上传Hook
 * 职责: 管理批量上传的状态和流程
 * 依赖: React, API, 子模块
 * 导出: useBatchUpload hook
 * 限制: 文件<280行, 函数<30行
 */

import { useState } from 'react';
import { App } from 'antd';
import { periodAPI } from '../../../../services/api';
import type { Period } from '../../../../types';
import type { UploadResult, UploadProgress } from '../../types';
import { validateFiles, extractErrorMessage } from '../../utils/fileUtils';
import { showUploadResult } from '../../components/UploadResultModal';
import {
  preprocessFiles,
  deleteOldPeriods,
  type ProgressUpdater
} from './fileProcessor';
import { waitForTaskCompletion } from './taskMonitor';
import { performBatchUpload, processBatchResults } from './uploadExecutor';

interface UseBatchUploadOptions {
  skuId: number;
  periods: Period[];
  onUploadComplete: () => void;
}

/**
 * 批量上传Hook
 */
export const useBatchUpload = ({
  skuId,
  periods,
  onUploadComplete
}: UseBatchUploadOptions) => {
  const { message } = App.useApp();
  const [batchUploading, setBatchUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<UploadProgress>({});

  /**
   * 进度更新辅助函数
   */
  const updateProgress: ProgressUpdater = (fileName, status) => {
    setUploadProgress(prev => ({ ...prev, [fileName]: status }));
  };

  /**
   * 处理上传后的完成流程
   */
  const handleUploadCompletion = async (
    taskId: string | undefined,
    successCount: number,
    batchResults: any[]
  ): Promise<void> => {
    if (taskId && successCount > 0) {
      // 有task_id说明需要异步处理关键词数据
      await waitForTaskCompletion(
        taskId,
        batchResults,
        periodAPI.checkBatchTask,
        updateProgress
      );
      await onUploadComplete();
    } else if (successCount > 0) {
      // 没有task_id但有成功的（可能Celery未启动或已处理完）
      message.success(`成功上传 ${successCount} 个周期`);
      // 延迟1秒再刷新，给后端时间完成基础数据保存
      await new Promise(resolve => setTimeout(resolve, 1000));
      await onUploadComplete();
    } else {
      // 全部失败，不刷新
      message.error('所有文件上传失败，请检查错误信息');
    }
  };

  /**
   * 处理批量上传错误
   */
  const handleUploadError = (
    error: any,
    validFiles: File[],
    results: UploadResult[]
  ): void => {
    console.error('Batch upload error:', error);
    const errorMsg = extractErrorMessage(error);

    // 标记所有文件失败
    validFiles.forEach(file => {
      updateProgress(file.name, '❌ 失败');
      results.push({
        file: file.name,
        success: false,
        message: errorMsg
      });
    });
  };

  /**
   * 处理批量上传
   */
  const handleBatchUpload = async (fileList: File[]): Promise<void> => {
    if (fileList.length === 0) return;

    // 验证文件类型
    const { validFiles, invalidFiles } = validateFiles(fileList);
    if (invalidFiles.length > 0) {
      message.error(
        `以下文件格式不支持: ${invalidFiles.map(f => f.name).join(', ')}`
      );
      return;
    }

    setBatchUploading(true);
    setUploadProgress({});

    const results: UploadResult[] = [];

    try {
      // 第一步：预处理文件
      const { fileInfos, results: preResults } = preprocessFiles(
        validFiles,
        updateProgress
      );
      results.push(...preResults);

      if (fileInfos.length === 0) {
        showUploadResult(results);
        setBatchUploading(false);
        setUploadProgress({});
        return;
      }

      // 第二步：删除旧周期
      await deleteOldPeriods(fileInfos, periods, periodAPI.delete);

      // 第三步：批量上传
      const { batchResults, taskId } = await performBatchUpload(
        fileInfos,
        skuId,
        periodAPI.batchUpload,
        updateProgress
      );

      // 第四步：处理结果
      processBatchResults(batchResults, results, updateProgress);

      // 第五步：等待异步任务完成
      const successCount = results.filter(r => r.success).length;
      await handleUploadCompletion(taskId, successCount, batchResults);

    } catch (error: any) {
      handleUploadError(error, validFiles, results);
    } finally {
      setBatchUploading(false);
      setUploadProgress({});
      showUploadResult(results);
    }
  };

  return {
    batchUploading,
    uploadProgress,
    handleBatchUpload
  };
};

